import { calculateOfferCargoProcessStatus } from "@/features/offer-cargo-process-status";
import {
  type OFFER_CARGO_PROCESS_RECEPTION_STATUS,
  calculateOfferCargoProcessReceptionStatus,
} from "@/features/offer-cargo-process-status/badge-status";
import type { DraftOfferCargo } from "@/pages/shipper-cargo-list/model";
import { OFFER_CARGO_VEHICLE_SHAPES_LIST } from "@/shared/constants/offer-cargo";
import { excludeNull } from "@/shared/lib/collection";
import {
  fromIso8601Date,
  fromIso8601DateTime,
  toLocaleTextAsDate,
  toLocaleTextAsDateTime,
} from "@/shared/lib/datetime";
import { type Loadable, transformLoadable } from "@/shared/lib/loadable";

export type CargoTableData = {
  id: number;
  departure: string;
  startAt: string;
  destination: string;
  endAt: string;
  transitCount: number;
  vehicle: string;
  receptionStatus: Loadable<OFFER_CARGO_PROCESS_RECEPTION_STATUS>;
  expiration: string;
  proposalCount: Loadable<number>;
};

export const mapDraftOfferCargos = (draftOfferCargos: DraftOfferCargo[]) =>
  draftOfferCargos.map<CargoTableData>((draftOfferCargo) => {
    const firstDestination = draftOfferCargo.destinations[0];
    const lastDestination =
      draftOfferCargo.destinations[draftOfferCargo.destinations.length - 1];
    let vehicleShapeLabel = "-";
    for (const shape of OFFER_CARGO_VEHICLE_SHAPES_LIST) {
      if (draftOfferCargo.vehicleShape === shape.value) {
        vehicleShapeLabel = shape.label;
      }
    }
    const offerCargoProcessStatus = transformLoadable(
      draftOfferCargo.process,
      (process) => {
        return calculateOfferCargoProcessStatus({
          process: {
            draftOfferCargo: { ...draftOfferCargo, process },
            deliveryProposalOfferCargos: process.deliveryProposalOfferCargos,
            contractOfferCargos: excludeNull([
              process.contractProposalOfferCargo,
              process.contractConcludedOfferCargo,
            ]),
          },
        });
      },
    );
    const receptionStatus: CargoTableData["receptionStatus"] =
      transformLoadable(offerCargoProcessStatus, (status) => {
        return calculateOfferCargoProcessReceptionStatus({
          viewer: "shipper",
          status,
        });
      });
    const proposalCount: CargoTableData["proposalCount"] = transformLoadable(
      draftOfferCargo.process,
      (process) => {
        return process.deliveryProposalOfferCargos.length;
      },
    );
    return {
      id: draftOfferCargo.id,
      departure: `${firstDestination.prefecture} ${firstDestination.address}`,
      startAt: toLocaleTextAsDate(fromIso8601Date(draftOfferCargo.loadingOn)),
      destination: `${lastDestination.prefecture} ${lastDestination.address}`,
      endAt: toLocaleTextAsDate(fromIso8601Date(draftOfferCargo.unloadingOn)),
      transitCount: draftOfferCargo.destinations.length - 2,
      vehicle: `${vehicleShapeLabel}/${draftOfferCargo.vehicleWeight}t`,
      receptionStatus,
      expiration: toLocaleTextAsDateTime(
        fromIso8601DateTime(draftOfferCargo.deadlineAt),
      ),
      proposalCount,
    };
  });
